<template>
  <div class="container-fluid py-4 dashboard-boutique">

    <!-- ================= HEADER ================= -->
    <div class="header-boutique d-flex justify-content-between align-items-center flex-wrap mb-4">

      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-speedometer2 fs-2"></i>
        <div>
          <h2 class="fw-bold mb-0">Dashboard Boutique</h2>
          <small>Suivi global des ventes, stock et performance</small>
        </div>
      </div>

      <!-- FILTRES -->
      <div class="d-flex gap-2 flex-wrap">

        <button
          v-for="p in periodes"
          :key="p.value"
          class="btn"
          :class="filtre === p.value ? 'btn-active' : 'btn-outline-light'"
          @click="changerFiltre(p.value)"
        >
          {{ p.label }}
        </button>

      </div>

    </div>

    <!-- ================= LOADER ================= -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-success"></div>
      <p>Chargement...</p>
    </div>

    <!-- ================= CONTENT ================= -->
    <div v-else>

      <!-- ================= STOCK ================= -->
      <h5 class="section-title">📦 Stock</h5>
      <div class="row g-3 mb-4">

        <div class="col-md-3" v-for="card in stockKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body">
              <div class="d-flex justify-content-between">
                <div>
                  <small>{{ card.title }}</small>
                  <h4>{{ card.value }}</h4>
                </div>
                <i :class="card.icon"></i>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- ================= VENTES ================= -->
      <h5 class="section-title">💰 Ventes</h5>
      <div class="row g-3 mb-4">

        <div class="col-md-3" v-for="card in ventesKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body">
              <div class="d-flex justify-content-between">
                <div>
                  <small>{{ card.title }}</small>
                  <h4>{{ card.value }}</h4>
                </div>
                <i :class="card.icon"></i>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- ================= APPRO ================= -->
      <h5 class="section-title">📥 Approvisionnement</h5>
      <div class="row g-3 mb-4">

        <div class="col-md-3" v-for="card in approKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body">
              <div class="d-flex justify-content-between">
                <div>
                  <small>{{ card.title }}</small>
                  <h4>{{ card.value }}</h4>
                </div>
                <i :class="card.icon"></i>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>

  </div>
</template>

<script>
import api from "@/api";

export default {

  data() {
    return {
      isLoading: false,
      filtre: "jour",

      dashboard: {
        produits: {},
        stock: {},
        ventes: {},
        approvisionnement: {}
      },

      periodes: [
        { value: "jour", label: "Aujourd'hui" },
        { value: "semaine", label: "Semaine" },
        { value: "mois", label: "Mois" },
        { value: "annee", label: "Année" }
      ]
    };
  },

  computed: {

    // ================= STOCK =================
    stockKpi() {
      const s = this.dashboard.stock || {};

      return [
        { title: "Lots", value: s.lots ?? 0, icon: "bi bi-box bg-primary" },
        { title: "Valeur stock", value: this.money(s.valeur_stock), icon: "bi bi-cash bg-success" },
        { title: "Ruptures", value: s.ruptures ?? 0, icon: "bi bi-exclamation-triangle bg-danger" },
        { title: "Stock critique", value: s.stock_critique ?? 0, icon: "bi bi-exclamation-circle bg-warning" }
      ];
    },

    // ================= VENTES =================
    ventesKpi() {
      const v = this.dashboard.ventes || {};

      return [
        { title: "Total ventes", value: v.total ?? 0, icon: "bi bi-cart bg-primary" },
        { title: "CA", value: this.money(v.chiffre_affaires), icon: "bi bi-currency-exchange bg-success" },
        { title: "Annulées", value: v.annulees ?? 0, icon: "bi bi-x-circle bg-danger" },
        { title: "Quantité", value: v.quantite_vendue ?? 0, icon: "bi bi-boxes bg-info" }
      ];
    },

    // ================= APPRO =================
    approKpi() {
      const a = this.dashboard.approvisionnement || {};

      return [
        { title: "Entrées", value: a.total ?? 0, icon: "bi bi-arrow-down bg-primary" },
        { title: "Quantité", value: a.quantite_entree ?? 0, icon: "bi bi-stack bg-success" },
        { title: "Valeur", value: this.money(a.valeur), icon: "bi bi-wallet bg-warning" }
      ];
    }

  },

  mounted() {
    this.load();
  },

  methods: {

    async load() {
      this.isLoading = true;

      try {
        const res = await api.getItems(
          `dashboard_data?periode=${this.filtre}`
        );

        // 🔥 FIX IMPORTANT (évite écran vide)
        this.dashboard = res?.data || res || {
          produits: {},
          stock: {},
          ventes: {},
          approvisionnement: {}
        };

      } catch (e) {
        console.error("Dashboard error:", e);
      } finally {
        this.isLoading = false;
      }
    },

    changerFiltre(f) {
      this.filtre = f;
      this.load();
    },

    money(v) {
      return Number(v || 0).toLocaleString("fr-FR") + " FCFA";
    }

  }
};
</script>

<style scoped>

/* ================= BACKGROUND ================= */
.dashboard-boutique {
  background: #f5f7fb;
  min-height: 100vh;
}

/* ================= HEADER ================= */
.header-boutique {
  background: linear-gradient(135deg, #0f5132, #198754);
  color: white;
  padding: 20px;
  border-radius: 18px;
}

/* ================= SECTION TITLE ================= */
.section-title {
  margin: 20px 0 10px;
  font-weight: bold;
  color: #333;
}

/* ================= CARD KPI ================= */
.card-kpi {
  border: none;
  border-radius: 14px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: 0.2s;
}

.card-kpi:hover {
  transform: translateY(-4px);
}

/* ICON STYLE */
.card-kpi i {
  font-size: 28px;
  padding: 10px;
  border-radius: 12px;
  color: white;
}

/* ACTIVE BUTTON */
.btn-active {
  background: white;
  color: #198754;
  font-weight: bold;
}

</style>